import React, {Fragment, useCallback, useEffect, useState} from 'react';
import MaterialTable from 'material-table';
import {
    Backdrop,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Divider,
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    makeStyles,
    Modal,
    Paper,
    Typography,
} from "@material-ui/core";
import useStyles from '../styles/styles';
import useTrafficStyles from '../styles/trafficStyles';
import {Alert} from '@material-ui/lab';
import {useMutation, useQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import ClearIcon from '@material-ui/icons/Clear';
import CheckIcon from '@material-ui/icons/Check';
import DeleteIcon from '@material-ui/icons/Delete';
import theme from '../styles/theme';
import {eventPayoutFields, trafficSourceFields} from '../utils/TrafficSourceMap';
import useFormInputs from "../hooks/useFormInputs";
import _ from 'lodash';
import {navigate} from '@reach/router';

import PanelBox from '../components/TrafficSource/PanelBox';
import RenderInput from '../components/TrafficSource/RenderInput';
import getEnvironment from "../utils/getEnvironment";
import setDate from "../utils/setDate";
import TableIcons from "../utils/TableIcons";

import AddBoxIcon from "@material-ui/icons/AddBox";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import TrafficSourceOfferInput from "../components/TrafficSource/TrafficSourceOfferInput";
import PromoRefreshConfirmationModal from "../components/TrafficSource/PromoRefreshConfirmationModal";

const useRevenueStyles = makeStyles({
    root: {
        border: '1px solid',

        '& .MuiExpansionPanelSummary-root': {
            padding: theme.spacing(0, 2, 0, 1),
        },
    },
    removeButton: {
        cursor: 'pointer',
        pointerEvents: 'all'
    },
});

const TrafficSource = ({trafficSourceId}) => {
    const classes = useStyles();
    const env = getEnvironment();
    const trafficClasses = useTrafficStyles();
    const [editView, setEditView] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [errors, setErrors] = useState([]);
    const [defaultValues, setDefaultValues] = useState({});
    const [isModalOpen, setIsModalOpen] = useState('');
    const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
    const [manualLoad, setManualLoad] = useState(false);
    const [revenuePayoutRows, setRevenuePayoutRows] = useState([]);
    const [expandedPanelsArr, setExpandedPanelsArr] = useState([]);
    const [selectedUrls, setSelectedUrls] = useState([]);
    const [modalOfferUrls, setModalOfferUrls] = useState([]);
    const scheduleClasses = useRevenueStyles();
    const [showOfferSuccess, setShowOfferSuccess] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState("");

    const [nextPromoId, setNextPromoId] = useState('');
    const onSubmit = () => {
        let error = false;
        let errorMessages = []
        setErrors(error);
        let nonRequiredFields = [];
        trafficSourceFields.forEach((section) => {
            section.arr.forEach((item) => {
                if (!item.required) {
                    nonRequiredFields.push(item.key);
                }
            });
        });
        Object.keys(inputs).forEach(key => {
            if (nonRequiredFields.includes(key)) {
                return
            }
            const value = inputs[key]
            if (value === '') {
                console.error(`${key} is a required field.`)
                error = true
                errorMessages[0] = 'Please add values to the highlighted fields!'
            }
        });

        const offer = findOfferById(data.allOffer, inputs["offerId"])

        if (offer && offer.multiOffer) {
            if (!conversionEventPayoutExists()) {
                console.error(`Required event payout not found for attached offer that is multiOffer`)
                error = true
                errorMessages[1] = 'Please add a Conversion - Rev Share event payout because of the attached offer being multiOffer!'
            }
        }

        if (inputs['pinpoint'] === true && !(inputs['medium'] === 'SMS' || inputs['medium'] === 'EMAIL')) {
            error = true
            errorMessages[1] = 'Medium must be SMS or EMAIL for Pinpoint Traffic Sources'
        }

        if (!error) {
            openModal('edit');
        } else {
            setErrors(errorMessages)
        }
    };

    const {
        clearAllInputs,
        inputs,
        handleSwitchChange,
        handleFilterChange,
        handleInputChange,
        handleSubmit,
        setInputs
    } = useFormInputs(onSubmit);

    const {loading, error, data, networkStatus, refetch} = useQuery(gql`
        query TrafficSourcePage($trafficSourceId: UUID!) {
            trafficSource(uuid: $trafficSourceId) {
                id
                name
                aggregatorOnly
                channel
                createdAt
                everflowAffiliateHash
                includeS3InSource
                internal
                exclusive
                isListManageable
                isTest
                maximumPrice
                medium
                minimumPrice
                origin
                rootPingTree
                soldFeed
                sourceUrl
                offerUrl
                timeout
                multiOffer
                multiConversion
                unsoldFeed
                splitTest
                targetOfferUrlId
                trafficSourceSelectionStrategy
                affiliate {
                    id
                    name
                }
                payoutStrategy {
                    id
                    enum
                    name
                }
                proxyToOlp
                legacyZoneId
                legacyOfferId
                legacyPromoId
                priceRejectMins {
                    mins
                    config {
                        noPennies
                        checkNextTier
                        returnAnyBid
                    }
                }
                isDeleted
                eventPayouts {
                    id
                    trafficSourceId
                    event
                    payoutStrategy
                    payoutType
                    payout
                    field
                    dataPackageType
                    companyId
                    billed
                    revShare
                }
                promoHistory {
                    id
                    dateCreated
                    promoId
                }
                multiTierSubmit
                offer {
                    advertiserId
                    id
                    offerName
                    offerUrls {
                        httpMethod
                        id
                        offerId
                        name
                        offerUrlQueryParameters {
                            constantValue
                            id
                            offerUrlId
                            parameterName
                            sourceData
                        }
                        urlPath
                    }
                }
                pinpoint
            }
            allAffiliate {
                nodes {
                    id
                    name
                }
            }
            allTrafficSource {
                nodes {
                    id
                    name
                    legacyPromoId
                }
            }
            allPingTree {
                nodes {
                    id
                }
            }
            allAffiliatePayoutStrategy {
                nodes {
                    id
                    enum
                    name
                }
            }
            allCompanies {
                id
                name
            }
            allTrafficOrigin: __type(name: "TrafficOrigin") {
                enumValues {
                    name
                }
            }
            allTrafficChannels: __type(name: "TrafficChannel") {
                enumValues {
                    name
                }
            }
            allTrafficMedium: __type(name: "TrafficMedium") {
                enumValues {
                    name
                }
            }
            allFeeds: __type(name: "ListFeed") {
                enumValues {
                    name
                }
            }
            allRevenueEvents: __type(name: "RevenueEvent") {
                enumValues {
                    name
                }
            }
            allEventPayoutStrategyTypes: __type(name: "EventPayoutStrategyType") {
                enumValues {
                    name
                }
            }
            allPayoutTypes: __type(name: "PayoutType") {
                enumValues {
                    name
                }
            }
            allDataPackageTypes: __type(name: "DataPackageType") {
                enumValues {
                    name
                }
            }
            allFields: __type(name: "Field") {
                enumValues {
                    name
                }
            }
            findAllTrafficSourceListsByTrafficSourceId(id: $trafficSourceId) {
                category
                key
                includeAndExclude
            }
            allTrafficSourceOfferUrlByTrafficSourceId(trafficSourceId: $trafficSourceId) {
                shortCode
                trafficSourceId
                offerUrlId
                active
                longId
            }
            allTrafficSourceSelectionStrategy: __type(name: "TrafficSourceSelectionStrategy") {
                enumValues {
                    name
                }
            }
            allOffer {
                id
                offerName
                advertiserId
                multiOffer
                offerUrls {
                    httpMethod
                    id
                    offerId
                    name
                    offerUrlQueryParameters {
                        constantValue
                        id
                        offerUrlId
                        parameterName
                        sourceData
                    }
                    urlPath
                }
            }
            allAdvertiser {
                id
                name
            }
        }
    `, {
        variables: {
            trafficSourceId
        },
        notifyOnNetworkStatusChange: true,
    });

    const [updateTrafficSource, {data: mutationData, loading: mutationLoading, error: mutationError}] = useMutation(gql`
        mutation UpdateTrafficSource(
            $id: UUID!
            $name: String!
            $legacyZoneId: Int
            $legacyPromoId: Int
            $legacyOfferId: Int
            $origin: TrafficOrigin!
            $medium: TrafficMedium!
            $channel: TrafficChannel!
            $affiliateId: UUID!
            $internal: Boolean!
            $exclusive: Boolean!
            $includeS3InSource: Boolean!
            $minimumPrice: BigDecimal
            $maximumPrice: BigDecimal
            $timeout: Int
            $sourceUrl: String
            $offerUrl: String
            $isListManageable: Boolean!
            $soldFeed: ListFeed!
            $unsoldFeed: ListFeed!
            $aggregatorOnly: Boolean!
            $rootPingTree: String!
            $isTest: Boolean!
            $proxyToOlp: Boolean!
            $splitTest: Boolean!
            $payoutStrategy: PayoutStrategy!
            $priceRejectMins: PriceRejectMinsInput!
            $eventPayouts: [EventPayoutMutatorInput!]
            $multiTierSubmit: Boolean!
            $offerId: UUID!
            $pinpoint: Boolean!
            $multiConversion: Boolean!
            $targetOfferUrlId: UUID,
            $trafficSourceSelectionStrategy: TrafficSourceSelectionStrategy!
        ) {
            updateTrafficSource(
                id: $id
                name: $name
                legacyZoneId: $legacyZoneId
                legacyPromoId: $legacyPromoId
                legacyOfferId: $legacyOfferId
                origin: $origin
                medium: $medium
                channel: $channel
                affiliateId: $affiliateId
                internal: $internal
                exclusive: $exclusive
                includeS3InSource: $includeS3InSource
                minimumPrice: $minimumPrice
                maximumPrice: $maximumPrice
                timeout: $timeout
                sourceUrl: $sourceUrl
                offerUrl: $offerUrl
                isListManageable: $isListManageable
                soldFeed: $soldFeed
                unsoldFeed: $unsoldFeed
                aggregatorOnly: $aggregatorOnly
                rootPingTree: $rootPingTree
                isTest: $isTest
                payoutStrategy: $payoutStrategy
                proxyToOlp: $proxyToOlp
                splitTest: $splitTest
                priceRejectMins: $priceRejectMins
                eventPayouts: $eventPayouts
                multiTierSubmit: $multiTierSubmit
                offerId: $offerId
                pinpoint: $pinpoint
                multiConversion: $multiConversion
                targetOfferUrlId: $targetOfferUrlId
                trafficSourceSelectionStrategy: $trafficSourceSelectionStrategy
            ) {
                id
                name
                aggregatorOnly
                channel
                createdAt
                everflowAffiliateHash
                includeS3InSource
                internal
                exclusive
                isListManageable
                isTest
                maximumPrice
                medium
                minimumPrice
                origin
                rootPingTree
                soldFeed
                sourceUrl
                offerUrl
                timeout
                unsoldFeed
                splitTest
                targetOfferUrlId
                trafficSourceSelectionStrategy
                affiliate {
                    id
                    name
                }
                payoutStrategy {
                    id
                    enum
                    name
                }
                proxyToOlp
                legacyZoneId
                legacyOfferId
                legacyPromoId
                priceRejectMins {
                    mins
                    config {
                        noPennies
                        checkNextTier
                        returnAnyBid
                    }
                }
                isDeleted
                eventPayouts {
                    id
                    trafficSourceId
                    event
                    payoutStrategy
                    payoutType
                    payout
                    field
                    dataPackageType
                    companyId
                    billed
                    revShare
                }
                promoHistory {
                    dateCreated
                    promoId
                }
                multiTierSubmit
                offer {
                    advertiserId
                    id
                    offerName
                    offerUrls {
                        httpMethod
                        id
                        offerId
                        name
                        offerUrlQueryParameters {
                            constantValue
                            id
                            offerUrlId
                            parameterName
                            sourceData
                        }
                        urlPath
                    }
                }
                pinpoint
                multiConversion
            }
        }
    `);

    const {loading: promoIdLoading, error: promoIdError, data: promoIdData, refetch: promoIdRefetch} = useQuery(gql`
        query GetNextPromoId {
            getNextPromoId {
                value
            }
        }
    `, {
        notifyOnNetworkStatusChange: true,
        context: {
            clientName: "kgraphql"
        }
    });

    const [incrementPromoId, {
        loading: incrementPromoIdLoading,
        error: incrementPromoIdError,
        data: incrementPromoIdData
    }] = useMutation(gql`
        mutation IncrementPromoId {
            incrementPromoId
        }
    `, {
        context: {
            clientName: "kgraphql"
        }
    });

    const [deleteTrafficSource, {loading: deleteLoading, data: deleteTrafficSourceData}] = useMutation(gql`
        mutation DeleteTrafficSource(
            $id: UUID!
        ) {
            deleteTrafficSource(id: $id)
        }
    `);

    const [createTrafficSourceOfferUrl, {
        loading: trafficSourceOfferUrlLoading,
        data: trafficSourceOfferUrlData
    }] = useMutation(gql`
        mutation createTrafficSourceOfferUrl($input: TrafficSourceOfferUrlMutatorInput!) {
            createTrafficSourceOfferUrl(input: $input) {
                id,
                trafficSourceId,
                longId,
                shortCode,
                active
            }
        }
    `, {
        context: {
            clientName: "kgraphql"
        }
    });

    const setData = (objData, objKey) => {
        trafficSourceFields.forEach((section => {
            section.arr.forEach((item => {
                const {key, type} = item
                if (inputs[item.key] === undefined) {
                    const dataKey = objData[objKey][key];
                    let value = dataKey === '' ? '' : dataKey;
                    if (type === 'switch' && !value) {
                        value = false;
                    }

                    if (key === 'affiliateName') {
                        value = objData[objKey].affiliate.name;
                    }

                    if (key === 'priceRejectMins.mins') {
                        value = objData[objKey]['priceRejectMins'].mins.toString();
                    }

                    if (key === 'priceRejectMins.config.noPennies') {
                        value = objData[objKey]['priceRejectMins'].config.noPennies;
                    }

                    if (key === 'priceRejectMins.config.checkNextTier') {
                        value = objData[objKey]['priceRejectMins'].config.checkNextTier;
                    }

                    if (key === 'priceRejectMins.config.returnAnyBid') {
                        value = objData[objKey]['priceRejectMins'].config.returnAnyBid;
                    }

                    if (key === 'offerId') {
                        value = objData[objKey].offer.id
                    }

                    if (_.isObject(value)) {
                        value = value.enum;
                    }

                    setInputs(inputs => ({
                        ...inputs,
                        [key]: value
                    }));
                    setDefaultValues(inputs => ({
                        ...inputs,
                        [key]: value
                    }));
                }
            }));
        }));

        const eventPayouts = data.trafficSource.eventPayouts;
        if (eventPayouts.length) {
            let finalArr = [];
            eventPayouts.forEach((item) => {
                let obj = {};
                let arr = [];
                eventPayoutFields.forEach(row => {
                    obj = {
                        ...row,
                        value: item[row.key]
                    }
                    arr.push(obj);
                });
                arr.push({
                    id: item.id,
                    key: 'id'
                }, {
                    delete: false,
                    key: 'delete'
                });
                finalArr.push(arr);
            });
            setRevenuePayoutRows(finalArr);
        }
    };

    const findOfferById = useCallback((allOffer, offerId) => {
        return allOffer.find(offer => offer.id === offerId)
    }, [])

    const addEventPayoutRowWithEventAndPayoutType = (event, payoutType) => {
        const values = [...revenuePayoutRows];

        if (!data.allRevenueEvents.enumValues.find(e => e.name === event) || !data.allPayoutTypes.enumValues.find(t => t.name === payoutType)) {
            return
        }

        const newSet = eventPayoutFields.map(item => {
            let valueToSet = ''
            if (item.key === "event") {
                valueToSet = event
            } else if (item.key === "payoutType") {
                valueToSet = payoutType
            }
            return {
                ...item,
                value: valueToSet,
            };
        });
        values.push(newSet);
        setExpandedPanelsArr(expandedPanelsArr => [...expandedPanelsArr, values.length - 1]);
        setRevenuePayoutRows(values);
    };

    const conversionEventPayoutExists = () => {

        return revenuePayoutRows.find(row => {
                const isBeingDeleted = row.find(field => field.key === 'delete')?.value === true
                const hasRevshare = row.find(field => field.key === 'revShare')?.value
                const isInternal = row.find(field => field.key === 'payoutStrategy')?.value === 'INTERNAL'
                const isConversion = row.find(field => field.key === 'event')?.value === "CONVERSION"

                return isConversion
                    && !isBeingDeleted
                    && (hasRevshare || isInternal)
            }
        )
    }

    const getOfferUrls = (offer, trafficSourceOfferUrls) => {
        if (!offer || !offer.offerUrls) {
            return []
        }

        const offerUrlIds = {}
        const arr = [];
        trafficSourceOfferUrls.forEach((item) => {
            if (item.active) {
                offerUrlIds[item.offerUrlId] = [item.longId, item.shortCode]
            }
        })

        return data.trafficSource.offer.offerUrls.filter((item) => item.id in offerUrlIds).map(offerUrl => ({
            longId: offerUrlIds[offerUrl.id][0],
            shortCode: offerUrlIds[offerUrl.id][1],
            offerUrl: `${offerUrl.name} - ${offerUrl.urlPath}${offerUrl.offerUrlQueryParameters.map((queryParam, i) => {
                const firstChar = i === 0 ? '?' : '&'
                const value = queryParam.constantValue ? queryParam.constantValue : `{${queryParam.sourceData}}`
                return `${firstChar}${queryParam.parameterName}=${value}`
            }).join('')}`
        }))
    }

    const addOfferUrl = () => {
        setIsModalOpen('offerUrl');
    }

    const onSave = () => {
        const affiliateId = data.allAffiliate.nodes.filter(item => item.name === inputs.affiliateName);
        console.log("saving, inputs are ", inputs)
        const finalInputs = _.pickBy(inputs, v => v !== '');
        delete finalInputs.everflowAffiliateHash;
        clearAllInputs();
        setDefaultValues({});
        let minsArr = [];

        if (finalInputs["priceRejectMins.mins"]) {
            const priceRejectMins = inputs["priceRejectMins.mins"].split(',');
            priceRejectMins.forEach(item => {
                if (item !== ' ') {
                    minsArr.push(parseFloat(item));
                }
            });
        }

        let finalRevenuePayouts = []
        revenuePayoutRows.forEach((rows) => {
            let obj = {};
            rows.forEach((item) => {
                if (item.value !== '' && (item.key !== 'id')) {
                    obj = {
                        ...obj,
                        [item.key]: item.value
                    };
                } else if (item.key === 'id') {
                    obj = {
                        ...obj,
                        id: item.id,
                    };
                }

                obj = {
                    ...obj,
                    delete: item.delete || false
                };
            });
            if (Object.keys(obj).length) {
                finalRevenuePayouts.push(obj);
            }
        });
        updateTrafficSource({
            variables: {
                ...finalInputs,
                id: trafficSourceId,
                affiliateId: affiliateId.length ? affiliateId[0].id : null,
                priceRejectMins: {
                    mins: minsArr,
                    config: {
                        noPennies: finalInputs["priceRejectMins.config.noPennies"],
                        checkNextTier: finalInputs["priceRejectMins.config.checkNextTier"],
                        returnAnyBid: finalInputs["priceRejectMins.config.returnAnyBid"],
                    }
                },
                eventPayouts: finalRevenuePayouts
            }
        });
    }

    const offer = () => {

        if (!data.trafficSource.offer && !editView) {
            return null
        }


        const value = data.trafficSource.offer ?
            {
                advertiser: data.allAdvertiser.find((advertiser) => {
                    return advertiser.id === data.trafficSource.offer.advertiserId
                }),
                offer: data.trafficSource.offer
            } : {}


        return (
            <Box className={`${classes.filterBox} ${trafficClasses.panelBox}`} style={{marginTop: '10px'}}>
                <Chip
                    label={'Offer'}
                    className={classes.filterSectionLabels}
                />
                <Grid container>
                    <Grid item xs={9}>
                        <TrafficSourceOfferInput
                            value={value}
                            handleSwitchChange={handleSwitchChange}
                            handleInputChange={handleInputChange}
                            setInputs={setInputs}
                            inputs={inputs}
                            data={data}
                            disabled={!editView}
                            advertiserId={data.trafficSource.offer.advertiserId}
                            offerId={data.trafficSource.offer.id}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        {editView && (data.trafficSource.offer || inputs && inputs.offerId) ? (
                            <Button
                                variant="contained"
                                className={classes.addButton}
                                onClick={() => addOfferUrl()}
                                size="small"
                            >
                                Add Offer Url <AddBoxIcon/>
                            </Button>
                        ) : null}
                    </Grid>
                </Grid>
                <Divider style={{margin: '30px 0'}}/>
                <MaterialTable
                    title="Traffic Source Offer Urls"
                    icons={TableIcons}
                    columns={[
                        {title: "Short Code", field: "shortCode"},
                        {title: "OfferUrl", field: "offerUrl"},
                    ]}
                    options={{
                        search: false
                    }}
                    data={getOfferUrls(data.trafficSource.offer, data.allTrafficSourceOfferUrlByTrafficSourceId)}
                />
            </Box>
        )
    }

    useEffect(() => {
            if (incrementPromoIdError) {
                setConfirmationModalIsOpen(false)
                const message = incrementPromoIdError ? incrementPromoIdError.message : ""
                let err = [...errors]
                if (message) {
                    err = [...err, message]
                }

                setErrors(err);
            }
        },
        [incrementPromoIdError]
    )

    useEffect(() => {
        if (promoIdData) {
            setNextPromoId(promoIdData.getNextPromoId.value);
        }
    }, [promoIdData])

    useEffect(() => {
        if (editView && data && Object.keys(data).length) {
            const inputOfferId = inputs["offerId"]
            if (inputOfferId) {
                if (selectedOffer !== inputOfferId) {
                    setSelectedOffer(inputOfferId)
                }
            }
        }
    }, [inputs])

    useEffect(() => {
        if (editView && data && Object.keys(data).length) {
            const offer = findOfferById(data.allOffer, selectedOffer)
            if (offer && offer.multiOffer) {
                if (!conversionEventPayoutExists()) {
                    addEventPayoutRowWithEventAndPayoutType("CONVERSION", "REV_SHARE")
                }
            }
        }
    }, [selectedOffer])

    useEffect(() => {
        if (isSaved) {
            setTimeout(() => setIsSaved(false), 5000);
            setManualLoad(false);
        }
    }, [isSaved]);

    useEffect(() => {
        if (data && Object.keys(data).length && data.trafficSource) {
            const urlsId = data.allTrafficSourceOfferUrlByTrafficSourceId.filter(item => item.active).map(item => item.offerUrlId);

            setSelectedUrls(urlsId);
            setData(data, 'trafficSource');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (data && data.trafficSource) {
            setDefaultValues(prevState => ({
                ...prevState,
                offerName: data.trafficSource.offer.offerName,
                advertiserId: data.trafficSource.offer.advertiserId,
                offerId: data.trafficSource.offer.id
            }));
        }
    }, [data])

    useEffect(() => {
        if (mutationData && Object.keys(mutationData).length) {
            if (modalOfferUrls.length) {
                let obj = {
                    offerUrlIds: modalOfferUrls.map((offerUrlId) => {
                        return {
                            offerUrlId: offerUrlId,
                            active: true
                        }
                    }),
                    trafficSourceId
                };

                createTrafficSourceOfferUrl({
                    variables: {
                        input: obj
                    }
                });
            } else {
                setEditView(false);
                closeModal();
                setIsSaved(true);
                setData(mutationData, 'updateTrafficSource');
                refetch();
                promoIdRefetch();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mutationData]);

    useEffect(() => {
        if (trafficSourceOfferUrlData && Object.keys(trafficSourceOfferUrlData).length) {
            setEditView(false);
            closeModal();
            setIsSaved(true);
            refetch();
            promoIdRefetch();
        }
    }, [trafficSourceOfferUrlData])

    useEffect(() => {
        if (deleteTrafficSourceData) {
            setTimeout(() => navigate('/info/traffic-sources'), 5000);
        }
    }, [deleteTrafficSourceData])

    const findOptionName = (options, id) => {
        const option = options.find(item => item.id === id)
        return option ? option.name : null
    }

    const findOptionId = (options, name) => {
        const option = options.find(item => item.name === name)
        return option ? option.id : null
    }

    const deleteRow = (deleteIndex) => {
        const values = [...revenuePayoutRows];
        let obj = [...values[deleteIndex]];
        const newSet = obj.map(item => {
            if (item.key === 'delete') {
                item.delete = true;
            }
            return {
                ...item,
            };
        });
        values[deleteIndex] = [...newSet];
        setRevenuePayoutRows(values);
    };

    const addEventPayoutRow = () => {
        const values = [...revenuePayoutRows];
        const newSet = eventPayoutFields.map(item => {
            return {
                ...item,
                value: ''
            };
        });
        values.push(newSet);
        setRevenuePayoutRows(values);
    };

    const handleEventPayoutChanges = (idx, rowIdx, newVal, type, key) => {
        let values = [...revenuePayoutRows];
        const obj = [...values[idx]];

        if (type === 'dateTime') {
            obj[rowIdx] = {
                ...obj[rowIdx],
                value: newVal.toISOString()
            };
        } else if (key === 'companyId') {
            const options = data[obj[rowIdx].val]
            const optionId = findOptionId(options, newVal)
            obj[rowIdx] = {
                ...obj[rowIdx],
                value: optionId
            };
        } else {
            obj[rowIdx] = {
                ...obj[rowIdx],
                value: newVal
            };
        }

        values[idx] = [...obj];
        setRevenuePayoutRows(values);
    }

    if (loading || networkStatus === 4 || deleteLoading || manualLoad || mutationLoading || trafficSourceOfferUrlLoading) {
        return (
            <Backdrop className={classes.backdrop} open={true}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        );
    }

    if (error || !data.trafficSource) {
        return (
            <Paper className={classes.paper}>
                <Alert severity="error">Well, this in embarrassing&hellip; something went wrong.</Alert>
                <p>
                    <a href="/#" onClick={(e) => {
                        e.preventDefault();
                        navigate('/info/traffic-sources');
                    }}>Click here to view all traffic sources.</a>
                </p>
            </Paper>
        );
    }

    const openModal = (type) => {
        setIsModalOpen(type);
    };
    const closeModal = () => {
        setIsModalOpen('');
    };

    const RowChanges = ({rowData}) => {
        const {title, arr} = rowData;
        return (
            <Container maxWidth={false} disableGutters className={trafficClasses.rowDiff}>
                {title && (
                    <Box className={trafficClasses.rowDiffSectionHeader}>
                        <Typography variant="body1">
                            <strong>{title}</strong>
                        </Typography>
                    </Box>
                )}

                <Box>
                    {arr.map(item => {
                        const {key, label, type} = item;
                        const newInputKey = inputs[key];
                        const currentInputKey = defaultValues[key];

                        if (type === 'trafficSourceOfferInput') {
                            const newOffer = data.allOffer.filter(item => item.id === newInputKey);
                            const currentOffer = data.allOffer.filter(item => item.id === currentInputKey);

                            let newAdvertiser = 'N/A';
                            let currentAdvertiser = 'N/A'
                            let newOfferName = 'N/A';
                            let currentOfferName = 'N/A';

                            if (newOffer && newOffer.length) {
                                newOfferName = newOffer[0].offerName || 'N/A'
                                const newAdv = data.allAdvertiser.filter(item => item.id === newOffer[0].advertiserId);

                                if (newAdv.length) {
                                    newAdvertiser = newAdv[0].name;
                                }
                            }

                            if (currentOffer && currentOffer.length) {
                                currentOfferName = currentOffer[0].offerName || 'N/A'
                                const currentAdv = data.allAdvertiser.filter(item => item.id === currentOffer[0].advertiserId);

                                if (currentAdv.length) {
                                    currentAdvertiser = currentAdv[0].name;
                                }
                            }
                            return (
                                <Grid container key={key}
                                      className={`row - grid ${newInputKey !== currentInputKey ? trafficClasses.rowDiffHighlight : ''}`}>
                                    <Grid item xs={4}>
                                        <Typography variant="body">
                                            Advertiser Name
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        {currentAdvertiser}
                                    </Grid>
                                    <Grid item xs={4}>
                                        {newAdvertiser}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body">
                                            Offer Name
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        {currentOfferName}
                                    </Grid>
                                    <Grid item xs={4}>
                                        {newOfferName}
                                    </Grid>
                                </Grid>
                            );
                        }

                        return (
                            <Grid container key={key}
                                  className={`row - grid ${newInputKey !== currentInputKey ? trafficClasses.rowDiffHighlight : ''}`}>
                                {label ? (
                                    <Grid item xs={4}>
                                        <Typography variant="body">
                                            {label}
                                        </Typography>
                                    </Grid>
                                ) : null}
                                <Grid item xs={4}>
                                    {type === 'switch' ? (
                                        <>
                                            {currentInputKey ? (
                                                <CheckIcon style={{color: theme.palette.success.main}}/>
                                            ) : (
                                                <ClearIcon style={{color: theme.palette.error.main}}/>
                                            )}
                                        </>
                                    ) : (
                                        <span>{_.isNil(currentInputKey) || currentInputKey === '' ? 'N/A' : currentInputKey}</span>
                                    )}
                                </Grid>
                                <Grid item xs={4}>
                                    {type === 'switch' ? (
                                        <>
                                            {newInputKey ? (
                                                <CheckIcon style={{color: theme.palette.success.main}}/>
                                            ) : (
                                                <ClearIcon style={{color: theme.palette.error.main}}/>
                                            )}
                                        </>
                                    ) : (
                                        <span>{_.isNil(newInputKey) || newInputKey === '' ? 'N/A' : newInputKey}</span>
                                    )}
                                </Grid>
                            </Grid>
                        );
                    })}
                </Box>
            </Container>
        );
    };

    return (
        <>
            <Typography variant="h2" component="h2" className={classes.monospace}>
                Traffic Sources
            </Typography>
            <Paper className={classes.paper}>
                <Container maxWidth={false} className={trafficClasses.root}>
                    {data && data.trafficSource && data.trafficSource.isDeleted ? (
                        <Alert severity="error">This traffic source has been deleted!</Alert>
                    ) : null}
                    <form id="traffic-source-form">
                        {isSaved ? (
                            <Alert severity="success">Updates Saved!</Alert>
                        ) : false}
                        {errors && errors.length ? errors.map((error) => {
                                return (<Alert severity="error">{error}</Alert>)
                            }
                        ) : false}
                        <Box className={trafficClasses.headerBox}>
                            <Grid container spacing={3} alignItems="flex-end">
                                {trafficSourceFields.map((section, idx) => {
                                    const {title, arr} = section;
                                    if (!title) {
                                        return (
                                            <Fragment key={idx.toString()}>
                                                {arr.map(item => {
                                                    const {key, label} = item;
                                                    return (
                                                        <Grid key={key} item xs={4}
                                                              className={trafficClasses.header}>
                                                            <Typography variant="body1">
                                                                <strong>{label}</strong>
                                                            </Typography>
                                                            {!editView ? (
                                                                <Typography variant="body1">
                                                                    {inputs[key]}
                                                                </Typography>
                                                            ) : (
                                                                <RenderInput
                                                                    item={item}
                                                                    value={inputs[key]}
                                                                    data={data}
                                                                    handleFilterChange={handleFilterChange}
                                                                    handleInputChange={handleInputChange}
                                                                    handleSwitchChange={handleSwitchChange}
                                                                    setInputs={setInputs}
                                                                    inputs={inputs}
                                                                />
                                                            )}
                                                        </Grid>
                                                    );
                                                })}
                                            </Fragment>
                                        );
                                    }
                                    return null;
                                })}
                                {env === 'test' ? (
                                    <Grid item xs={4}
                                          className={`${trafficClasses.header} ${trafficClasses.headerButtons} `}>
                                        {!editView ? (
                                            <>
                                                <Button variant="contained" color="primary"
                                                        onClick={() => setConfirmationModalIsOpen(true)}>
                                                    Promo Refresh
                                                </Button>
                                                <Button variant="contained" color="secondary"
                                                        onClick={() => setEditView(true)}>
                                                    Edit
                                                </Button>
                                                <Button variant="outlined" className={classes.deleteButton}
                                                        onClick={() => openModal('delete')}
                                                        startIcon={<DeleteIcon/>}>
                                                    Delete
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button variant="contained" color="primary" onClick={handleSubmit}>
                                                    Save Changes
                                                </Button>
                                                <Button variant="contained" color="secondary" onClick={() => {
                                                    setInputs(defaultValues);
                                                    setEditView(false);
                                                }}>
                                                    Cancel
                                                </Button>
                                            </>
                                        )}
                                    </Grid>
                                ) : null}
                            </Grid>
                        </Box>
                        <Box>
                            <Grid container spacing={3} direction="row" justify="center" alignItems="stretch">
                                {trafficSourceFields.map((section, idx) => {
                                    if (section.display !== false) {
                                        return (
                                            section.title &&
                                            <PanelBox
                                                key={`${section.title} -${idx.toString()} `}
                                                panelData={section}
                                                trafficClasses={trafficClasses}
                                                editView={editView}
                                                inputs={inputs}
                                                data={data}
                                                handleFilterChange={handleFilterChange}
                                                handleInputChange={handleInputChange}
                                                handleSwitchChange={handleSwitchChange}
                                                setInputs={setInputs}
                                                nextPromoId={nextPromoId}
                                            />
                                        )
                                    }
                                })}
                            </Grid>
                        </Box>
                        <Divider style={{margin: '30px 0'}}/>
                        {showOfferSuccess ? (
                            <Alert severity="success">Offer added to traffic source but to finalize the changes
                                please
                                hit the Save Changes button on the top of the page.</Alert>
                        ) : null}
                        {offer()}
                        <Divider style={{margin: '30px 0'}}/>
                        <Box className={`${classes.filterBox} ${trafficClasses.panelBox}`}
                             style={{marginTop: '10px'}}>
                            <Chip
                                label={'Event Payouts'}
                                className={classes.filterSectionLabels}
                            />
                            {editView ? (
                                <Button
                                    style={{float: 'left'}}
                                    variant="contained"
                                    className={classes.addButton}
                                    onClick={() => addEventPayoutRow()}
                                    size="small"
                                >
                                    Add Row <AddBoxIcon/>
                                </Button>
                            ) : null}
                            <Grid container direction="row" alignItems="flex-start" spacing={2}>
                                <Grid item xs={12} style={{padding: '10px 0'}}>
                                    {revenuePayoutRows.map((rows, idx) => {
                                        let isDeleted = rows.filter(obj => obj.delete);
                                        if (isDeleted.length) {
                                            return null;
                                        }
                                        const isExpanded = expandedPanelsArr.length ? expandedPanelsArr.includes(idx) : false;
                                        return (
                                            <ExpansionPanel
                                                key={idx.toString()}
                                                id={`adjustments-${idx}`}
                                                expanded={isExpanded}
                                                className={scheduleClasses.root}
                                                onChange={(e, bool) => {
                                                    if (bool) {
                                                        setExpandedPanelsArr(expandedPanelsArr => [...expandedPanelsArr, idx]);
                                                    } else {
                                                        if (expandedPanelsArr.includes(idx)) {
                                                            setExpandedPanelsArr(expandedPanelsArr.filter(panel => panel !== idx));
                                                        }
                                                    }
                                                }}
                                            >
                                                <ExpansionPanelSummary
                                                    expandIcon={<ExpandMoreIcon/>}
                                                >
                                                    <Grid
                                                        container
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={3}
                                                    >
                                                        <Grid container key={idx.toString()}
                                                              style={{margin: '10px 0'}}>
                                                            <Grid item xs={12} md={3}>
                                                                <Box display="flex" flexDirection="row"
                                                                     alignItems="center">
                                                                    <Box display="flex" flexDirection="column"
                                                                         alignItems="center">
                                                                        {!editView ? (
                                                                            <IconButton
                                                                                className={scheduleClasses.removeButton}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                }}>

                                                                            </IconButton>
                                                                        ) : (
                                                                            <IconButton
                                                                                className={scheduleClasses.removeButton}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    deleteRow(idx);
                                                                                }}>
                                                                                <RemoveCircleIcon/>
                                                                            </IconButton>
                                                                        )}
                                                                    </Box>
                                                                    <Box textAlign="left">
                                                                        <Typography variant="body1">
                                                                            <strong>{isExpanded ? 'Close Revenue Payout' : 'View Revenue Payout'}</strong>
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Grid>
                                                            {!isExpanded ? (
                                                                <Grid item xs={9}>
                                                                    <Grid container>
                                                                        {rows.map((item, rowIdx) => {
                                                                            const {key, label} = item;
                                                                            if (key !== 'id' && key !== 'revenueId' && key !== 'delete') {
                                                                                return (
                                                                                    <Grid item xs={4} key={rowIdx}
                                                                                          style={{
                                                                                              paddingBottom: '10px',
                                                                                              textAlign: 'left'
                                                                                          }}>
                                                                                        <Typography variant="body1"
                                                                                                    style={{paddingBottom: '5px'}}>
                                                                                            <strong>{label}</strong>
                                                                                        </Typography>
                                                                                        <Typography variant="body1">
                                                                                            {key === 'companyId' ? (
                                                                                                <span>{_.isNil(item.value) || item.value === '' ? 'N/A' : findOptionName(data[item.val], item.value)}</span>
                                                                                            ) : (
                                                                                                <span>{_.isNil(item.value) || item.value === '' ? 'N/A' : item.value}</span>
                                                                                            )}
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        })}
                                                                    </Grid>
                                                                </Grid>
                                                            ) : null}
                                                        </Grid>
                                                    </Grid>
                                                </ExpansionPanelSummary>
                                                <ExpansionPanelDetails className={classes.details}>
                                                    <Grid
                                                        container
                                                        direction="row"
                                                        spacing={3}
                                                    >
                                                        {rows.map((item, rowIdx) => {
                                                            const {key, label} = item;
                                                            if (key !== 'id' && key !== 'delete') {
                                                                return (
                                                                    <Grid item xs={4} key={rowIdx} style={{
                                                                        paddingBottom: '10px',
                                                                        textAlign: 'left'
                                                                    }}>
                                                                        <Typography variant="body1"
                                                                                    style={{paddingBottom: '5px'}}>
                                                                            <strong>{label}</strong>
                                                                        </Typography>
                                                                        {!editView ? (
                                                                            <Typography variant="body1">
                                                                                {key === 'companyId' ? (
                                                                                    <span>{_.isNil(item.value) || item.value === '' ? 'N/A' : findOptionName(data[item.val], item.value)}</span>
                                                                                ) : (
                                                                                    <span>{_.isNil(item.value) || item.value === '' ? 'N/A' : item.value}</span>
                                                                                )}
                                                                            </Typography>
                                                                        ) : (
                                                                            <RenderInput
                                                                                item={item}
                                                                                disabled={item.disabled || false}
                                                                                value={key === 'companyId' ? findOptionName(data[item.val], item.value) : revenuePayoutRows[idx][rowIdx].value}
                                                                                data={data}
                                                                                handleFilterChange={(newVal) => handleEventPayoutChanges(idx, rowIdx, newVal, item.type, key)}
                                                                                handleInputChange={(e) => handleEventPayoutChanges(idx, rowIdx, e.target.value, item.type, key)}
                                                                                handleSwitchChange={handleSwitchChange}
                                                                                setInputs={setInputs}
                                                                                inputs={inputs}
                                                                            />
                                                                        )}
                                                                    </Grid>
                                                                );
                                                            }
                                                            return null;
                                                        })}
                                                    </Grid>
                                                </ExpansionPanelDetails>
                                            </ExpansionPanel>
                                        );
                                    })}
                                </Grid>
                            </Grid>
                        </Box>
                        <Divider style={{margin: '30px 0'}}/>
                        <Box>
                            <MaterialTable
                                title="Lead Source Blocks"
                                icons={TableIcons}
                                columns={[
                                    {title: "Key", field: "key"},
                                    {title: "Category", field: "category"},
                                    {title: "Include & Exclude", field: "includeAndExclude"},
                                ]}
                                data={data.findAllTrafficSourceListsByTrafficSourceId.map(config => ({
                                    category: config.category,
                                    key: config.key,
                                    includeAndExclude: config.includeAndExclude,
                                }))}
                            />
                        </Box>
                        <Divider style={{margin: '30px 0'}}/>
                        <Box>
                            <MaterialTable
                                title="Promo History"
                                icons={TableIcons}
                                columns={[
                                    {title: "Id", field: "id"},
                                    {title: "Date Created", field: "dateCreated"},
                                    {title: "Promo Id", field: "promoId"}
                                ]}
                                data={data.trafficSource.promoHistory.map(promoHistory => ({
                                    id: promoHistory.id,
                                    dateCreated: setDate(promoHistory.dateCreated),
                                    promoId: promoHistory.promoId
                                }))}
                            />
                        </Box>
                    </form>
                </Container>
                <Modal
                    open={isModalOpen === 'edit'}
                    onClose={closeModal}
                >
                    <Container className={`${classes.modal} ${trafficClasses.modal} `} maxWidth={false}>
                        <Typography variant="h5" align="center" gutterBottom>
                            Please confirm your traffic source changes
                        </Typography>
                        <Paper className={classes.paper} style={{marginBottom: 0}}>

                            <Box>
                                <Grid container className="row-grid">
                                    <Grid item xs={4}/>
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle1" align="center">
                                            CURRENT CHANGES
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle1" align="center">
                                            NEW CHANGES
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box>
                                {trafficSourceFields.map((section, idx) => (
                                    <RowChanges key={section.title} rowData={section}/>
                                ))}
                            </Box>
                            {defaultValues["legacyPromoId"] !== inputs["legacyPromoId"] ? (
                                <Box>
                                    <Container maxWidth={false} disableGutters className={trafficClasses.rowDiff}>
                                        <Box className={trafficClasses.rowDiffSectionHeader}>
                                            <Typography variant="body1">
                                                <strong>Lead Source Blocks</strong>
                                            </Typography>
                                        </Box>
                                        {data.findAllTrafficSourceListsByTrafficSourceId.map(config => {
                                            if (config.includeAndExclude) {
                                                return (
                                                    <Grid container key={config.key} className={`row - grid`}>
                                                        <Grid item xs={4}>
                                                            <Typography variant="body">
                                                                {config.key}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="body">
                                                                {trafficSourceId}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="body">
                                                                {trafficSourceId}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                );
                                            } else {
                                                return (
                                                    <Grid container key={config.key}
                                                          className={`row - grid ${trafficClasses.rowDiffHighlight}`}>
                                                        <Grid item xs={4}>
                                                            <Typography variant="body">
                                                                {config.key}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="body">
                                                                {trafficSourceId}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Typography variant="body">{``}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                );
                                            }
                                        })}
                                    </Container>
                                </Box>
                            ) : null}
                            <Grid item xs className={trafficClasses.headerButtons}>
                                <Button variant="contained" color="primary" onClick={(e) => {
                                    e.preventDefault();
                                    onSave();
                                }}>
                                    Save
                                </Button>
                                <Button variant="outlined" onClick={() => closeModal()}>
                                    Cancel
                                </Button>
                            </Grid>
                        </Paper>
                    </Container>
                </Modal>
                <Modal
                    open={isModalOpen === 'delete'}
                    onClose={closeModal}
                >
                    <Container className={`${classes.modal}`} maxWidth={false}>
                        <Paper className={classes.paper} style={{marginBottom: 0}}>
                            <Typography variant="h5" align="center" gutterBottom>
                                Are you sure you want to delete the traffic source - {data.trafficSource.name}?
                            </Typography>
                            <Grid item xs className={trafficClasses.headerButtons}
                                  style={{textAlign: 'center', margin: '1rem 0'}}>
                                <Button variant="contained" color="primary" onClick={(e) => {
                                    e.preventDefault();
                                    setManualLoad(true);
                                    deleteTrafficSource({
                                        variables: {
                                            id: trafficSourceId
                                        }
                                    });
                                }}>
                                    Confirm
                                </Button>
                                <Button variant="outlined" onClick={() => closeModal()}>
                                    Cancel
                                </Button>
                            </Grid>
                        </Paper>
                    </Container>
                </Modal>
                {isModalOpen === 'offerUrl' ? (
                    <Modal
                        open={true}
                        onClose={closeModal}
                    >
                        <Container className={`${classes.modal}`} maxWidth={false}>
                            <Paper className={classes.paper} style={{marginBottom: 0}}>
                                <Typography variant="h5" align="center" gutterBottom>
                                    Select Offer Urls Below
                                </Typography>
                                {data.trafficSource.offer ? (
                                    <FormControl className={classes.formControl}>
                                        <FormGroup>
                                            <Grid container>
                                                {data.trafficSource.offer.offerUrls.map(urls => {
                                                    const isChecked = selectedUrls.length ? selectedUrls.includes(urls.id) : false;
                                                    return (
                                                        <Grid item xs={6} key={urls.id}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={isChecked}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setSelectedUrls(prevState => [...prevState, urls.id]);
                                                                            } else {
                                                                                const arr = selectedUrls.filter(item => item !== urls.id);
                                                                                setSelectedUrls(arr);
                                                                            }
                                                                        }}
                                                                        name={urls.id}
                                                                    />
                                                                }
                                                                label={
                                                                    <Box
                                                                        display="flex"
                                                                        alignItems="flex-start"
                                                                        justifyContent="space-between"
                                                                        flexDirection="column"
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            style={{paddingBottom: '5px'}}
                                                                        >
                                                                            {urls.name} <br/>
                                                                            <small>{urls.urlPath}</small>
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                            />
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </FormGroup>
                                    </FormControl>
                                ) : (
                                    <div className={classes.loadingBar}>
                                        <CircularProgress
                                            size={50}
                                            thickness={4}
                                        />
                                    </div>
                                )}
                                <Grid item xs className={trafficClasses.headerButtons}
                                      style={{textAlign: 'center', margin: '1rem 0'}}>
                                    <Button variant="contained" color="primary" onClick={(e) => {
                                        e.preventDefault();
                                        setModalOfferUrls(selectedUrls);
                                        setShowOfferSuccess(true);
                                        closeModal();
                                    }}>
                                        Confirm
                                    </Button>
                                    <Button variant="outlined" onClick={() => closeModal()}>
                                        Cancel
                                    </Button>
                                </Grid>
                            </Paper>
                        </Container>
                    </Modal>
                ) : null}
                {confirmationModalIsOpen ?
                    <PromoRefreshConfirmationModal inputs={inputs}
                                                   setInputs={setInputs}
                                                   onSave={onSave}
                                                   closeModal={() => setConfirmationModalIsOpen(false)}
                                                   nextPromoId={nextPromoId}
                                                   incrementPromoIdMutation={incrementPromoId}
                                                   trafficSourceId={trafficSourceId}
                                                   findAllTrafficSourceListsByTrafficSourceId={data.findAllTrafficSourceListsByTrafficSourceId}
                    /> : null}
            </Paper>
        </>
    );
};

export default TrafficSource;
